import { FC } from 'react';
import { Grid, Typography } from '@mui/material';
import { format } from 'date-fns';

import { JobRequest } from 'domain/entities/JobRequest';
import { ServiceType } from 'domain/entities/ServiceType';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { capitalizeString } from 'infrastructure/utils/capitalizeString';
import { Container } from 'targets/web/modules/jobs/components/JobDetailsForm/common';
import { PreviewBox } from 'targets/web/components';

export const CustomerJobRequestFormContainer: FC<{ jobRequest: JobRequest }> = ({ jobRequest }) => {
  const t = useTranslationPrefix(
    'approvals.confirm_job_request_view.customer_job_request_form_container',
  );

  return (
    <Container width={1} gap={4} sx={{ padding: 4 }} data-testname="jobRequestContainer">
      <Typography variant="h6">{t('title')}</Typography>

      <Grid container spacing={4}>
        <Grid item xs={4}>
          <PreviewBox
            data-testname="customer"
            header={t('header.customer')}
            subheader={jobRequest?.customer?.name || 'n/a'}
            size="regular"
          />
        </Grid>
        <Grid item xs={4}>
          <PreviewBox
            data-testname="airport"
            header={t('header.airport')}
            subheader={jobRequest?.airport?.name || 'n/a'}
            size="regular"
          />
        </Grid>
        <Grid item xs={4}>
          <PreviewBox
            data-testname="facility"
            header={t('header.facility')}
            subheader={jobRequest?.facility?.name || 'n/a'}
            size="regular"
          />
        </Grid>

        {(jobRequest?.aircraftType ||
          jobRequest?.aircraftRegNumber ||
          jobRequest?.aircraftSerialNumber) && (
          <>
            <Grid item xs={4}>
              <PreviewBox
                data-testname="aircraftType"
                header={t('header.aircraft_type')}
                subheader={jobRequest?.aircraftType?.name || 'n/a'}
                size="regular"
              />
            </Grid>
            <Grid item xs={4}>
              <PreviewBox
                data-testname="aircraftRegNumber"
                header={t('header.aircraft_reg_number')}
                subheader={jobRequest?.aircraftRegNumber || 'n/a'}
                size="regular"
              />
            </Grid>
            <Grid item xs={4}>
              <PreviewBox
                data-testname="aircraftSerialNumber"
                header={t('header.aircraft_serial_number')}
                subheader={jobRequest?.aircraftSerialNumber || 'n/a'}
                size="regular"
              />
            </Grid>
          </>
        )}

        <Grid item xs={6}>
          <PreviewBox
            direction="column"
            alignItems="start"
            spacing={1}
            header={t('header.notes')}
            size="regular"
          >
            <Typography noWrap={false} variant="labelMedium">
              {jobRequest?.serviceNeeds ?? 'N/A'}
            </Typography>
          </PreviewBox>
        </Grid>
        {jobRequest?.requestedServices?.length > 0 && (
          <Grid item xs={6}>
            <PreviewBox
              direction="column"
              alignItems="start"
              spacing={1}
              header={t('header.services')}
              size="regular"
            >
              <Typography noWrap={false} sx={{ breakWord: 'break-all' }} variant="labelMedium">
                {jobRequest?.requestedServices
                  .map((s: ServiceType) => capitalizeString(s.name))
                  .join(', ')}
              </Typography>
            </PreviewBox>
          </Grid>
        )}
        {jobRequest?.specialServiceNote && (
          <Grid item xs={12}>
            <PreviewBox
              direction="column"
              alignItems="start"
              spacing={1}
              header={t('header.special_service_notes')}
              size="regular"
            >
              <Typography noWrap={false} variant="labelMedium">
                {jobRequest?.specialServiceNote ?? 'N/A'}
              </Typography>
            </PreviewBox>
          </Grid>
        )}

        <Grid item xs={4}>
          <PreviewBox
            direction="column"
            alignItems="start"
            spacing={1}
            header={t('header.price_list')}
            size="regular"
          >
            <Typography noWrap={false} variant="labelMedium">
              {jobRequest?.customer?.portalPriceList?.name ?? 'N/A'}
            </Typography>
          </PreviewBox>
        </Grid>
        <Grid item xs={4}>
          <PreviewBox
            direction="column"
            alignItems="start"
            spacing={1}
            header={t('header.arrival_date')}
            size="regular"
          >
            <Typography noWrap={false} variant="labelMedium">
              {jobRequest?.arrivalDate ? format(jobRequest?.arrivalDate, 'MM/dd/yyyy') : 'N/A'}
            </Typography>
          </PreviewBox>
        </Grid>
        <Grid item xs={4}>
          <PreviewBox
            direction="column"
            alignItems="start"
            spacing={1}
            header={t('header.departure_date')}
            size="regular"
          >
            <Typography noWrap={false} variant="labelMedium">
              {jobRequest?.departureDate ? format(jobRequest?.departureDate, 'MM/dd/yyyy') : 'N/A'}
            </Typography>
          </PreviewBox>
        </Grid>
      </Grid>
    </Container>
  );
};
